import React, { Component } from "react";
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";

const GET_ATTENDANCE_REPORT = gql`
  query ($student_user_exam_id: Int!, $month: String, $academic_year_id: Int) {
    getStudentAttendanceFeeReport(
      student_user_exam_id: $student_user_exam_id
      month: $month
      academic_year_id: $academic_year_id
    ) {
      attendance {
        student_id
        student_name
        total_present
        total_absent
        total_working_days
        percentage
        days {
          day
          status
        }
      }
    }
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Day status → colour. Codes: P = present (green), A = absent (red),
// NM = not marked (purple). Full words are also supported.
const STATUS_COLORS = {
    p: "#22c55e", present: "#22c55e",
    a: "#ef4444", ab: "#ef4444", absent: "#ef4444",
    nm: "#9ca3af", "not marked": "#9ca3af", notmarked: "#9ca3af",
    l: "#f59e0b", leave: "#f59e0b",
    h: "#0ea5e9", holiday: "#0ea5e9",
    wo: "#cbd5e1", weekoff: "#cbd5e1", sunday: "#cbd5e1",
    hd: "#14b8a6", "half-day": "#14b8a6", halfday: "#14b8a6",
};
const STATUS_LABELS = {
    p: "Present", present: "Present",
    a: "Absent", ab: "Absent", absent: "Absent",
    nm: "Not Marked", "not marked": "Not Marked", notmarked: "Not Marked",
    l: "Leave", leave: "Leave",
    h: "Holiday", holiday: "Holiday",
    wo: "Week Off", weekoff: "Week Off", sunday: "Week Off",
    hd: "Half Day", "half-day": "Half Day", halfday: "Half Day",
};
// Short code shown on each calendar day cell.
const STATUS_ABBR = {
    p: "P", present: "P",
    a: "A", ab: "A", absent: "A",
    nm: "NM", "not marked": "NM", notmarked: "NM",
    l: "L", leave: "L",
    h: "H", holiday: "H",
    wo: "WO", weekoff: "WO", sunday: "WO",
    hd: "HD", "half-day": "HD", halfday: "HD",
};
const key = (s) => `${s || ""}`.trim().toLowerCase();
const statusColor = (s) => STATUS_COLORS[key(s)] || "#e2e8f0";
const statusLabel = (s) => STATUS_LABELS[key(s)] || (s ? String(s) : "—");
const statusAbbr = (s) => STATUS_ABBR[key(s)] || (s ? String(s).slice(0, 2).toUpperCase() : "");

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

class AttendanceTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            month: moment().format("YYYY-MM"),
            attendance: null,
            dayFilter: null, // null | "working" | "present" | "absent" — highlights the calendar
            loading: false,
        };
    }

    // Does a status pass the active day filter? (null filter = everything shows.)
    matchesFilter = (status) => {
        const f = this.state.dayFilter;
        if (!f) return true;
        const k = key(status);
        if (f === "present") return ["p", "present"].includes(k);
        if (f === "absent") return ["a", "ab", "absent"].includes(k);
        if (f === "working") return ["p", "present", "a", "ab", "absent"].includes(k);
        return true;
    };

    // Clicking a stat card toggles the calendar filter.
    toggleDayFilter = (f) => this.setState((s) => ({ dayFilter: s.dayFilter === f ? null : f }));

    componentDidMount() {
        this.getReport(this.state.month);
    }

    getReport = async (month) => {
        this.setState({ loading: true });
        try {
            const result = await this.props.client.query({
                query: GET_ATTENDANCE_REPORT,
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    month,
                    academic_year_id: null,
                },
                fetchPolicy: "cache-first",
            });
            const data = result?.data?.getStudentAttendanceFeeReport;
            const attendance = Array.isArray(data?.attendance) ? data.attendance[0] : data?.attendance;
            this.setState({ attendance: attendance || null, loading: false });
        } catch (error) {
            console.log(error);
            this.setState({ attendance: null, loading: false });
        }
    };

    changeMonth = (delta) => {
        const month = moment(`${this.state.month}-01`).add(delta, "month").format("YYYY-MM");
        this.setState({ month }, () => this.getReport(month));
    };

    renderControls = () => (
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <button type="button" className="sp-af-card" style={monthBtn} onClick={() => this.changeMonth(-1)}>
                <i className="fas fa-angle-left" />
            </button>
            <span className="font-weight-bold text-dark" style={{ fontSize: "14px", minWidth: 120, textAlign: "center" }}>
                {moment(`${this.state.month}-01`).format("MMMM YYYY")}
            </span>
            <button type="button" className="sp-af-card" style={monthBtn} onClick={() => this.changeMonth(1)}>
                <i className="fas fa-angle-right" />
            </button>
        </div>
    );

    renderDonut = (att) => {
        const present = num(att.total_present);
        const absent = num(att.total_absent);
        const pct = att.percentage != null ? num(att.percentage) : (present + absent ? Math.round((present / (present + absent)) * 100) : 0);
        const color = pct >= 75 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444";
        const options = {
            chart: {
                type: "pie",
                backgroundColor: "transparent",
                height: 260,
                // Extra top/bottom margin so external labels never clip the ring.
                spacingTop: 12, spacingBottom: 12,
            },
            // Center label is rendered as an absolutely-centered HTML overlay in the
            // return below — NOT via Highcharts' title (useHTML title positions from
            // its top-left and never truly centers in the donut hole).
            title: { text: null },
            credits: { enabled: false },
            tooltip: { enabled: true, pointFormat: "<b>{point.y}</b> days" },
            // Clean inline slice labels (no long connector callouts that cramped the
            // chart). "Present 12" sits just outside its slice; tiny slices hide their
            // label to avoid overlap but stay in the tooltip.
            plotOptions: {
                pie: {
                    innerSize: "70%",
                    borderWidth: 0,
                    size: "80%",
                    // Pin the donut to the exact center of the container so the HTML
                    // overlay label lines up with the hole (external labels won't shift it).
                    center: ["50%", "50%"],
                    dataLabels: {
                        enabled: true,
                        distance: 12,
                        connectorWidth: 0,
                        formatter: function () {
                            // Hide labels for zero / very thin slices to keep it clean.
                            return this.y > 0 ? `${this.point.name} ${this.y}` : null;
                        },
                        style: { fontWeight: "700", fontSize: "11px", color: "#475569", textOutline: "none" },
                    },
                },
            },
            series: [
                {
                    animation: { duration: 900 },
                    data: [
                        { name: "Present", y: present, color: "#22c55e" },
                        { name: "Absent", y: absent, color: "#ef4444" },
                    ],
                },
            ],
        };
        // Wrap the chart so the % label can be an absolutely-centered overlay that
        // sits dead-center in the donut hole regardless of chart size/margins.
        return (
            <div style={{ position: "relative" }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
                <div
                    style={{
                        position: "absolute", top: "50%", left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center", pointerEvents: "none", lineHeight: 1.1,
                    }}
                >
                    <div style={{ fontSize: 26, fontWeight: 800, color }}>{pct}%</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".5px", color: "#94a3b8" }}>PRESENT</div>
                </div>
            </div>
        );
    };

    // Column chart of day counts per status (Present, Absent, Not Marked, Holiday…).
    renderStatusBar = (att) => {
        const counts = {};
        (att.days || []).forEach((d) => {
            const k = key(d.status);
            if (!k) return;
            counts[k] = (counts[k] || 0) + 1;
        });
        const entries = Object.keys(counts).map((k) => ({
            name: statusLabel(k),
            y: counts[k],
            color: statusColor(k),
        }));
        const options = {
            chart: { type: "column", backgroundColor: "transparent", height: 260 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories: entries.map((e) => e.name),
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600", fontSize: "11px" } },
            },
            yAxis: {
                min: 0,
                allowDecimals: false,
                title: { text: "Days", style: { color: "#94a3b8" } },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            legend: { enabled: false },
            tooltip: { enabled: true, pointFormat: "<b>{point.y}</b> day(s)" },
            plotOptions: {
                column: {
                    colorByPoint: true,
                    borderRadius: 6,
                    pointPadding: 0.08,
                    groupPadding: 0.12,
                    dataLabels: { enabled: true, style: { fontWeight: "700", color: "#475569", textOutline: "none" } },
                },
            },
            series: [{ name: "Days", animation: { duration: 900 }, data: entries }],
        };
        return entries.length > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
            <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No day-wise status to chart.</div>
        );
    };

    renderCalendar = (att) => {
        const days = att.days || [];
        const byDay = {};
        days.forEach((d) => {
            const n = typeof d.day === "number" ? d.day : (moment(d.day, ["YYYY-MM-DD", "D"]).isValid() ? moment(d.day, ["YYYY-MM-DD", "D"]).date() : parseInt(d.day));
            if (!isNaN(n)) byDay[n] = d.status;
        });
        const first = moment(`${this.state.month}-01`);
        const daysInMonth = first.daysInMonth();
        const leadOffset = first.day();

        const cells = [];
        for (let i = 0; i < leadOffset; i++) cells.push(<div key={`e${i}`} className="sp-cal-cell is-empty sp-pop" />);
        for (let d = 1; d <= daysInMonth; d++) {
            const status = byDay[d];
            // When a stat card is active, dim the days that don't match it.
            const dimmed = status ? !this.matchesFilter(status) : !!this.state.dayFilter;
            const bg = dimmed ? "#eef2f6" : (status ? statusColor(status) : "#eef2f6");
            cells.push(
                <div
                    key={d}
                    className="sp-cal-cell sp-pop"
                    title={status ? `${d} — ${statusLabel(status)}` : `${d}`}
                    style={{ background: bg, color: dimmed ? "#cbd5e1" : (status ? "#fff" : "#94a3b8"), opacity: dimmed ? 0.55 : 1, animationDelay: `${d * 12}ms`, flexDirection: "column", lineHeight: 1.05 }}
                >
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{d}</span>
                    {status ? (
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.3px", opacity: 0.95, marginTop: 1 }}>
                            {statusAbbr(status)}
                        </span>
                    ) : null}
                </div>
            );
        }

        const seen = [...new Set(days.map((d) => key(d.status)).filter(Boolean))];

        return (
            <div>
                {/* Legend first (code → meaning) so the calendar's P / WO / NM codes read clearly. */}
                {seen.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: "14px" }}>
                        {seen.map((s) => (
                            <span key={s} className="d-inline-flex align-items-center text-muted" style={{ fontSize: "12px", gap: 6 }}>
                                <span className="d-inline-flex align-items-center justify-content-center text-white" style={{ background: statusColor(s), width: 22, height: 18, borderRadius: 5, fontSize: 10, fontWeight: 700 }}>
                                    {statusAbbr(s)}
                                </span>
                                {statusLabel(s)}
                            </span>
                        ))}
                    </div>
                )}
                <div className="sp-cal-grid mb-3">
                    {WEEKDAYS.map((w) => <div key={w} className="sp-cal-head">{w}</div>)}
                    {cells}
                </div>
            </div>
        );
    };

    renderBody = (att) => {
        if (!att) {
            return <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>No attendance data for this month.</div>;
        }
        const stats = [
            { label: "Working Days", value: num(att.total_working_days), icon: "fa-calendar-day", bg: "linear-gradient(135deg,#6366f1,#4f46e5)", filter: "working" },
            { label: "Present", value: num(att.total_present), icon: "fa-user-check", bg: "linear-gradient(135deg,#22c55e,#16a34a)", filter: "present" },
            { label: "Absent", value: num(att.total_absent), icon: "fa-user-times", bg: "linear-gradient(135deg,#ef4444,#dc2626)", filter: "absent" },
            { label: "Percentage", value: `${att.percentage != null ? num(att.percentage) : 0}%`, icon: "fa-percent", bg: "linear-gradient(135deg,#f59e0b,#f97316)" },
        ];
        return (
            <>
                <Row>
                    {stats.map((s, i) => {
                        const active = s.filter && this.state.dayFilter === s.filter;
                        return (
                        <Col md={3} sm={6} key={s.label} className="mb-3">
                            <div
                                className={`sp-af-card sp-animate d-flex align-items-center p-3 ${s.filter ? "sp-summary-card" : ""}`}
                                onClick={s.filter ? () => this.toggleDayFilter(s.filter) : undefined}
                                title={s.filter ? `Highlight ${s.label.toLowerCase()} in the calendar` : undefined}
                                style={{ gap: 14, animationDelay: `${i * 70}ms`, cursor: s.filter ? "pointer" : "default", border: active ? "1.5px solid #4f46e5" : undefined, boxShadow: active ? "0 8px 20px rgba(79,70,229,0.18)" : undefined }}
                            >
                                <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                                    style={{ width: 46, height: 46, borderRadius: 12, background: s.bg }}>
                                    <i className={`fas ${s.icon}`} style={{ fontSize: 17 }} />
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>{s.label}</div>
                                    <div className="text-dark" style={{ fontSize: 24, fontWeight: 700, fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>{s.value}</div>
                                </div>
                            </div>
                        </Col>
                        );
                    })}
                </Row>
                <Row>
                    <Col lg={4} className="mb-4 mb-lg-0">
                        <div className="sp-af-card sp-animate p-4 h-100 d-flex flex-column justify-content-center" style={{ animationDelay: "120ms" }}>
                            <h6 className="font-weight-bold text-dark mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Attendance Rate</h6>
                            {this.renderDonut(att)}
                        </div>
                    </Col>
                    <Col lg={8}>
                        <div className="sp-af-card sp-animate p-4 h-100" style={{ animationDelay: "180ms" }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 8 }}>
                                <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                    {moment(`${this.state.month}-01`).format("MMMM YYYY")} — Daily Attendance
                                </h6>
                                {this.state.dayFilter ? (
                                    <button type="button" className="sp-filter-chip active" onClick={() => this.setState({ dayFilter: null })}>
                                        {this.state.dayFilter === "working" ? "Working Days" : this.state.dayFilter === "present" ? "Present" : "Absent"}
                                        <i className="fas fa-times ml-2" style={{ fontSize: 10 }} />
                                    </button>
                                ) : null}
                            </div>
                            {this.renderCalendar(att)}
                        </div>
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col sm={12}>
                        <div className="sp-af-card sp-animate p-4" style={{ animationDelay: "240ms" }}>
                            <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                Status Breakdown
                            </h6>
                            {this.renderStatusBar(att)}
                        </div>
                    </Col>
                </Row>
            </>
        );
    };

    render() {
        const { attendance, loading } = this.state;
        return (
            <TabSection
                icon="far fa-user-check"
                title="Attendance"
                desc="Monthly attendance calendar and summary."
                headerRight={this.renderControls()}
            >
                {loading ? <PageShimmer /> : this.renderBody(attendance)}
            </TabSection>
        );
    }
}

const monthBtn = {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4f46e5",
    cursor: "pointer",
};

export default compose(withApollo, withRouter)(AttendanceTab);
